import { MetricBenchmarkData } from '@strength/domain-workout';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { MdAdd } from '@teamworksdev/icons';
import { DateTime, Info } from 'luxon';
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  getWorkoutStatusDragBlockedReasonKey,
  isWorkoutStatusReadOnly,
} from '../../../components/badge/workout-status-badge';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../components/utils';
import { useFlags } from '../../../context/featureFlag';
import { useMoveWorkoutDate } from '../../../hooks/useMoveWorkoutDate';
import { toast } from '../../../hooks/useToast';
import { getLocale } from '../../../utils/i18n';
import { BlockPreview, WorkoutWithCycleAndSession } from '../types';
import { resolveAllViewDragEnd } from './all-view-drag';
import {
  buildCalendarWeeks,
  getCycleForDate,
  getMonthBoundaries,
  getSessionCountByCycle,
  groupWorkoutsByDate,
  isCycleAtCap,
} from './calendar-utils';
import { SingleWorkoutEditContext } from './SingleWorkoutSheet';
import { useScrollAwareMonthHeader } from './useScrollAwareMonthHeader';
import { WorkoutCalendarCard } from './WorkoutCalendarCard';
import { WorkoutEditPanel } from './WorkoutEditPanel';

/**
 * State for the inline edit panel.
 *
 * `edit` targets an existing workout by id. `add` opens a brand-new workout for
 * the clicked `date`; it also carries the in-memory `stub` (and its id) the
 * host built for that day, so the panel renders immediately and stays bound by
 * id once the first edit promotes the stub into a real, persisted workout.
 */
type OpenPanelState =
  | { mode: 'edit'; workoutId: string }
  | {
      mode: 'add';
      date: string;
      workoutId: string;
      workout: WorkoutWithCycleAndSession;
    };

type BlockAllViewProps = {
  block: BlockPreview;
  workouts: WorkoutWithCycleAndSession[];
  applyBenchmarkData: MetricBenchmarkData;
  editContext?: SingleWorkoutEditContext;
  overlayOpen?: boolean;
};

export function BlockAllView({
  block,
  workouts,
  applyBenchmarkData,
  editContext,
  overlayOpen,
}: BlockAllViewProps) {
  const { t } = useTranslation();
  const [openPanel, setOpenPanel] = useState<OpenPanelState | null>(null);
  const { dragInteractionsFlag } = useFlags();
  const { moveWorkoutDate, isWorkoutMoving } = useMoveWorkoutDate();

  // Editing requires host-provided mutation wiring.
  // forceReadOnly covers cross-team blocks that must remain read-only.
  const editingEnabled = !!editContext && !editContext.forceReadOnly;
  // Adding additionally requires the host to expose the add-workout wiring.
  const addingEnabled = editingEnabled && !!editContext?.addWorkout;

  // Drag shares the manual edit path's permissioning (SC-5441): no edit
  // rights in the builder means no drag, per workout and per flag.
  const dragEnabled = dragInteractionsFlag && editingEnabled;

  // Distance-constrained PointerSensor so a drag never swallows the click
  // that opens the workout panel.
  const dragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  // A workout whose status blocks drag (read-only, or in progress — its
  // logged history belongs to the original date) can still start a drag
  // while drag is enabled: the attempt warns here — explaining why the
  // status blocks the move — and the drag end rejects the move (the card
  // snaps back). Otherwise the attempt would fail silently.
  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      const workout = workouts.find(
        (w) => w.workoutId === String(event.active.id),
      );
      if (!workout) return;
      const reasonKey = getWorkoutStatusDragBlockedReasonKey(workout.status);
      if (!reasonKey) return;
      toast({
        title: t(reasonKey),
        severity: 'warning',
      });
    },
    [workouts, t],
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const resolved = resolveAllViewDragEnd({
        activeId: String(event.active.id),
        overId: event.over?.id != null ? String(event.over.id) : null,
        workouts,
      });
      if (!resolved) return;
      moveWorkoutDate({
        blockId: block.id,
        blockStartDate: block.startDate,
        blockEndDate: block.endDate,
        workoutId: resolved.workoutId,
        currentAssignedDate: resolved.currentAssignedDate,
        newAssignedDate: resolved.newAssignedDate,
        profileId: editContext?.selectedAthleteId ?? null,
      });
    },
    [block, workouts, moveWorkoutDate, editContext],
  );

  const openWorkoutId = openPanel?.workoutId ?? null;
  const openWorkout = openPanel
    ? (workouts.find((w) => w.workoutId === openPanel.workoutId) ??
      (openPanel.mode === 'add' ? openPanel.workout : null))
    : null;

  const onOpenPanel = editingEnabled
    ? (workoutId: string) => setOpenPanel({ mode: 'edit', workoutId })
    : undefined;

  const addWorkoutApi = addingEnabled ? editContext?.addWorkout : undefined;
  const onAddWorkout = addWorkoutApi
    ? (date: string) => {
        const workout = addWorkoutApi.create(date);
        if (workout) {
          setOpenPanel({
            mode: 'add',
            date,
            workoutId: workout.workoutId,
            workout,
          });
        }
      }
    : undefined;

  const closePanel = () => {
    editContext?.addWorkout?.cancel();
    setOpenPanel(null);
  };

  const locale = getLocale();
  const workoutsByDate = useMemo(
    () => groupWorkoutsByDate(workouts),
    [workouts],
  );
  // Session count per cycle (scoped to the workouts for the active layer),
  // used to hide the add CTA once a cycle hits the 7-session cap.
  const sessionCountByCycle = useMemo(
    () => getSessionCountByCycle(workouts),
    [workouts],
  );
  const weekdays = Info.weekdays('short', { locale }); // Mon–Sun
  const dayHeaders = [...weekdays.slice(6), ...weekdays.slice(0, 6)]; // Sun–Sat
  const weeks = useMemo(() => buildCalendarWeeks(block), [block]);
  const boundaries = useMemo(() => getMonthBoundaries(weeks), [weeks]);
  const boundaryByWeekIndex = useMemo(
    () => new Map(boundaries.map((b) => [b.weekIndex, b.isoDate])),
    [boundaries],
  );

  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const sentinelRefs = useRef(new Map<string, HTMLElement | null>());

  const monthLabel = useScrollAwareMonthHeader({
    weeks,
    boundaries,
    locale,
    sentinelRefs,
    scrollRoot,
  });

  return (
    <DndContext
      sensors={dragSensors}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div className="flex-1 min-h-0 flex w-full overflow-hidden">
        <div className="flex-1 min-w-0 min-h-0 flex flex-col p-4">
          <div className="border-x border-t border-gray-200 rounded-t-lg bg-white px-3 py-2 text-base font-semibold text-gray-900">
            {monthLabel}
          </div>
          <div
            ref={setScrollRoot}
            className="flex-1 min-h-0 overflow-auto grid grid-cols-7 auto-rows-max border border-gray-200 rounded-b-lg"
          >
            {dayHeaders.map((day) => (
              <div
                key={day}
                className="border-b border-r border-gray-200 bg-white px-2 py-2 text-center text-xs font-semibold uppercase text-gray-500"
              >
                {day}
              </div>
            ))}

            {weeks.flatMap((week, weekIndex) => {
              const boundaryDate = boundaryByWeekIndex.get(weekIndex);
              return week.days.map((cell, dayIndex) => {
                const label = parseInt(cell.date.slice(8, 10), 10);
                const isFirstCellOfBoundaryRow = dayIndex === 0 && boundaryDate;
                // Only persisted workouts render as cards; a not-yet-saved add
                // stub (exists === false) lives in the panel, not on the calendar.
                const cellWorkouts = (
                  workoutsByDate.get(cell.date) ?? []
                ).filter((workout) => workout.exists);
                // The add CTA is shown only on in-block days whose cycle resolves
                // and is below the session cap. A capped cycle hides it on every
                // one of its cells.
                const cellCycle = onAddWorkout
                  ? getCycleForDate(cell.date, block.sortedCycles)
                  : null;
                const showAddWorkout =
                  !!onAddWorkout &&
                  cell.isInBlock &&
                  !!cellCycle &&
                  !isCycleAtCap(sessionCountByCycle, cellCycle.id);
                return (
                  <DroppableDayCell
                    key={cell.date}
                    date={cell.date}
                    dragEnabled={dragEnabled}
                    className={cn(
                      'group relative min-h-32 border-b border-r border-gray-200 flex flex-col gap-1 pt-1 pr-2 pl-1 pb-1',
                      cell.isInBlock ? 'bg-white' : 'bg-gray-50',
                    )}
                  >
                    {isFirstCellOfBoundaryRow && (
                      <div
                        ref={(el) => {
                          if (el) {
                            sentinelRefs.current.set(boundaryDate, el);
                          } else {
                            sentinelRefs.current.delete(boundaryDate);
                          }
                        }}
                        data-month-year={boundaryDate}
                        aria-hidden="true"
                        className="absolute top-0 left-0 h-px w-px pointer-events-none"
                      />
                    )}
                    <span
                      className={cn(
                        'text-xs text-right',
                        cell.isInBlock ? 'text-gray-700' : 'text-gray-300',
                      )}
                    >
                      {label}
                    </span>
                    {cellWorkouts.map((workout) => (
                      <WorkoutCalendarCard
                        key={workout.workoutId}
                        workout={workout}
                        status={workout.status}
                        blockStatus={block.status}
                        applyBenchmarkData={applyBenchmarkData}
                        onOpenPanel={onOpenPanel}
                        isReadOnly={
                          !editingEnabled ||
                          isWorkoutStatusReadOnly(workout.status)
                        }
                        isActive={openWorkoutId === workout.workoutId}
                        hasPublishError={editContext?.allErrors.some(
                          (e) => e.workoutId === workout.workoutId,
                        )}
                        draggable={dragEnabled}
                        isMoving={isWorkoutMoving(workout.workoutId)}
                        dragAccessibleDate={
                          workout.assignedDate
                            ? DateTime.fromISO(workout.assignedDate, {
                                zone: 'utc',
                              })
                                .setLocale(locale)
                                .toLocaleString(DateTime.DATE_MED)
                            : undefined
                        }
                      />
                    ))}
                    {showAddWorkout && onAddWorkout && (
                      <button
                        type="button"
                        onClick={() => onAddWorkout(cell.date)}
                        className="flex cursor-pointer items-center gap-1 rounded px-1.5 py-1 text-left text-xs font-bold text-gray-500 opacity-0 transition hover:bg-gray-100 hover:text-gray-700 focus-visible:opacity-100 group-hover:opacity-100"
                      >
                        <MdAdd size={16} />
                        {t('addWorkout')}
                      </button>
                    )}
                  </DroppableDayCell>
                );
              });
            })}
          </div>
        </div>
        {editingEnabled && editContext && openWorkout && openPanel && (
          <div className="shrink-0 w-[30%] min-w-[360px] h-full border-l border-gray-200">
            <WorkoutEditPanel
              workout={openWorkout}
              editContext={editContext}
              onClose={closePanel}
              mode={openPanel.mode}
              overlayOpen={overlayOpen}
            />
          </div>
        )}
      </div>
    </DndContext>
  );
}

/**
 * One day cell of the All view grid, as a drop target for workout drags.
 * The droppable id is the cell's ISO date — the value a drop resolves to.
 * Disabled when drag is unavailable (flag off or read-only): no drop target
 * is reachable then, so drag cannot start. While a workout drag hovers the
 * cell, the cell highlights so the coach sees the day the workout would
 * land on.
 */
function DroppableDayCell({
  date,
  dragEnabled,
  className,
  children,
}: {
  date: string;
  dragEnabled: boolean;
  className: string;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: date,
    disabled: !dragEnabled,
  });
  return (
    <div
      ref={setNodeRef}
      data-date={date}
      className={cn(className, isOver && 'bg-blue-100')}
    >
      {children}
    </div>
  );
}
